// Delta color thresholds — single source of truth for green/yellow/red coloring
const DELTA_COLORS = { good: '#4ade80', warn: '#facc15', bad: '#f87171' };

/** Color for "delta vs best lap": only ~0 is green, then yellow < 1s, red >= 1s */
export const getDeltaBestLapColor = (delta: number): string =>
  delta < 0.05 ? DELTA_COLORS.good : delta < 1.0 ? DELTA_COLORS.warn : DELTA_COLORS.bad;

/** Color for "delta vs optimal": green < 0.5s, yellow < 2s, red >= 2s */
export const getDeltaOptimalColor = (delta: number): string =>
  delta < 0.5 ? DELTA_COLORS.good : delta < 2.0 ? DELTA_COLORS.warn : DELTA_COLORS.bad;

/**
 * Converts seconds to mm:ss.decimal format (omits minutes if 0)
 * @param seconds - Time in seconds
 * @param decimalPlaces - Number of decimal places to show (default: 2)
 * @returns Formatted time string (e.g., "2:13.90" or "45.30")
 */
export const formatTime = (seconds: number | null | undefined, decimalPlaces: number = 2): string => {
  if (seconds == null || isNaN(seconds)) {
    return '--:--';
  }

  const totalSeconds = Math.abs(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  
  // Format seconds with specified decimal places
  const formattedSeconds = remainingSeconds.toFixed(decimalPlaces);
  
  // If no minutes, return just seconds
  if (minutes === 0) {
    return formattedSeconds;
  }
  
  // Pad seconds for mm:ss format
  const paddedSeconds = formattedSeconds.padStart(
    decimalPlaces === 0 ? 2 : 3 + decimalPlaces, 
    '0'
  );
  
  return `${minutes}:${paddedSeconds}`;
};

/**
 * Formats time difference (usually positive for time lost)
 * @param seconds - Time difference in seconds
 * @param decimalPlaces - Number of decimal places to show (default: 2)
 * @returns Formatted time difference with + prefix (e.g., "+6.89" or "+1:06.89")
 */
export const formatTimeDifference = (seconds: number | null | undefined, decimalPlaces: number = 2): string => {
  if (seconds == null || isNaN(seconds)) {
    return '+--:--';
  }

  const formattedTime = formatTime(Math.abs(seconds), decimalPlaces);
  const sign = seconds >= 0 ? '+' : '-';
  
  return `${sign}${formattedTime}`;
};
